"use client";

// Blok VOUCHER PRODUK di /keranjang (mig 5610; tampilan revisi mig 5660).
//
// Tidak lagi memajang daftar voucher inline: satu baris ringkas "Terdapat
// voucher untuk produk di keranjang ini" + tombol **Pakai Voucher** menuju
// halaman /keranjang/voucher (kartu tiket bergaya Shopee). Pilihan dari halaman
// itu disimpan di localStorage (`cartVoucherSelection.ts`), dibaca di sini saat
// mount (hanya pilihan MILIK pembeli yang sedang login), lalu DIVALIDASI SERVER
// (`preview_my_voucher`): angka yang mengikat datang dari sana, dan voucher yang
// gugur (kuota habis, periode lewat, harga berubah, keranjang berubah) dilepas
// otomatis dengan kalimat sebabnya. Ringkasan Harga Umum · Potongan Voucher ·
// Diskon Level · Total tetap di sini dan diulang di atas tombol "Masukkan ke
// Pesanan" (keputusan Q12).
//
// Audit 2026-09-10 (sisi pembeli):
// • `selection` DIIKAT pada generasi harga (`sig` = tanda tangan keranjang saat
//   pratinjau sukses). Keranjang berubah / harga belum-atau-gagal termuat →
//   angka lama DIBUANG, tak pernah dipakai basi. Ini juga menutup kunci baris
//   posisional ("<ci+1>:<li+1>", `data.ts`) yang membuat total salah sesudah
//   satu baris dihapus.
// • Tiga keadaan harga dibedakan: menunggu · gagal (dengan "Coba lagi") ·
//   keranjang memang kosong — dulu ketiganya sama-sama "diam".
// • Selama voucher terpilih belum tervalidasi, `onPendingChange` menahan tombol
//   "Keep Produk" supaya pesanan tak dibuat tanpa/dengan voucher yang
//   angkanya belum pasti.
// • `notice` ("… dilepas: …") tetap dirender walau tak ada lagi kandidat —
//   voucher yang gugur memang hilang dari RPC kandidat (`voucher_is_live`).
//
// KODE VOUCHER (mig 6990): voucher ber-kode tak pernah ada di `candidates`
// (daftar pembeli menyembunyikannya). Kodenya diketik di /keranjang/voucher dan
// ikut pilihan localStorage → pratinjau & checkout membawanya (`p_code`). Blok
// ini juga tampil — dengan ajakan "Punya kode voucher?" — bila toko punya voucher
// produk ber-kode yang hidup walau daftar kandidatnya kosong; toko tanpa voucher
// ber-kode melihat keranjang yang sama persis seperti sebelumnya.

import { CheckCircle2, KeyRound, TicketPercent, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";

import type { CustomerData } from "@/context/CustomerContext";
import { buildBundleCartVoucherSummary, bundleVoucherLines } from "@/lib/bundleCheckout";
import type { CartState } from "@/lib/cartMulti";
import { clearCartVoucherPick, readCartVoucherPickFor, writeCartVoucherPick } from "@/lib/cartVoucherSelection";
import { formatPrice } from "@/lib/products";
import { resolveCartsPricing, type ResolvedBundle, type ResolvedCartPricing } from "@/lib/supabase/data";
import {
  fetchMyVoucherCandidates,
  fetchMyVoucherCodeKinds,
  previewMyVoucher
} from "@/lib/supabase/voucherStorefront";
import { voucherPreviewHoldsPick, type VoucherCandidate, type VoucherServerPreview } from "@/lib/voucher";
import { buildCartVoucherSummary, type CartVoucherSummary } from "@/lib/voucherCart";

export type { CartVoucherSummary };

export type CartVoucherSelection = {
  voucherId: string;
  voucherName: string;
  /** Kode yang diketik (voucher ber-kode, mig 6990) — wajib ikut checkout; null = voucher daftar. */
  code: string | null;
  preview: Extract<VoucherServerPreview, { ok: true }>;
};

/** Pilihan + generasi keranjang yang melahirkannya (lihat catatan di kepala berkas). */
type BoundSelection = CartVoucherSelection & { sig: string };

/** Harga baris keranjang: menunggu · siap (untuk satu generasi keranjang) · gagal. */
type PricingState =
  | { status: "loading" }
  | { status: "ready"; sig: string; rows: ResolvedCartPricing[] }
  | { status: "error"; sig: string };

/** Kartu ringkasan — dipakai di bawah daftar produk DAN di atas tombol "Keep Produk". */
export function CartVoucherSummaryCard({ summary }: { summary: CartVoucherSummary }) {
  return (
    <div className="rounded-xl border border-neutral-100 bg-shell p-3 text-xs">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-muted">Harga umum</span>
        <span className="font-bold text-ink">{formatPrice(summary.grossTotal)}</span>
      </div>
      {summary.voucherName ? (
        <div className="mt-1 flex items-center justify-between">
          <span className="font-semibold text-muted">Potongan voucher · {summary.voucherName}</span>
          <span className="font-bold text-leaf">−{formatPrice(summary.voucherAmount)}</span>
        </div>
      ) : null}
      {/* Audit A9: bagian PROMO kampanye (jejak potongan) dipisah dari diskon
          level; tanpa jejak, barisnya tetap "Diskon level / promo" gabungan. */}
      {summary.promoDiscount > 0 ? (
        <div className="mt-1 flex items-center justify-between">
          <span className="font-semibold text-muted">Potongan promo</span>
          <span className="font-bold text-leaf">−{formatPrice(summary.promoDiscount)}</span>
        </div>
      ) : null}
      {summary.levelDiscount > 0 ? (
        <div className="mt-1 flex items-center justify-between">
          <span className="font-semibold text-muted">
            {summary.promoDiscount > 0 ? "Diskon level" : "Diskon level / promo"}
          </span>
          <span className="font-bold text-leaf">−{formatPrice(summary.levelDiscount)}</span>
        </div>
      ) : null}
      {/* Bundel (mig 8040): langkah terakhir rantai potongan, sama dengan nota. */}
      {summary.bundleDiscount && summary.bundleDiscount > 0 ? (
        <div className="mt-1 flex items-center justify-between">
          <span className="font-semibold text-muted">Potongan bundling</span>
          <span className="font-bold text-leaf">−{formatPrice(summary.bundleDiscount)}</span>
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2">
        <span className="font-extrabold text-ink">Total</span>
        <span className="text-base font-extrabold text-shopee">{formatPrice(summary.total)}</span>
      </div>
      {summary.hasUnpriced ? (
        <p className="mt-1 text-[10px] font-semibold text-muted">
          Ada varian berharga menyusul — belum termasuk dalam total & tidak kena voucher.
        </p>
      ) : null}
    </div>
  );
}

export function CartVoucherSection({
  carts,
  customer,
  onSelectionChange,
  onSummaryChange,
  onCoverageChange,
  onPendingChange,
  bundleRows,
  bundleSignature = "",
  bundleProductIds
}: {
  carts: readonly CartState[];
  customer: CustomerData;
  onSelectionChange: (selection: CartVoucherSelection | null) => void;
  /** null = blok tak relevan (tak ada kandidat) → pemanggil tak merender ringkasan. */
  onSummaryChange: (summary: CartVoucherSummary | null) => void;
  /** Id produk keranjang yang tercakup minimal satu voucher yang boleh dicoba — untuk keterangan per kartu produk. */
  onCoverageChange?: (productIds: ReadonlySet<string>) => void;
  /**
   * true = ada voucher terpilih yang angkanya BELUM pasti (pratinjau jalan /
   * harga belum termuat / harga gagal termuat) → pemanggil menahan tombol
   * "Keep Produk". Selalu dilaporkan false saat blok ini dilepas.
   */
  onPendingChange?: (pending: boolean) => void;
  /**
   * BUNDEL di keranjang (mig 8040, K5 — voucher boleh di atas bundel).
   * `undefined` = keranjang tanpa bundel: perilaku lama PERSIS. `null` = harga
   * bundel belum siap → diperlakukan seperti harga keranjang yang belum
   * termuat (pilihan ditahan). Array = bundel yang harganya siap: komponennya
   * ikut pratinjau server dengan harga TERPISAH (muatan yang sama dengan RPC
   * `checkout_orders_with_bundles`), lalu ringkasan menerapkan langkah bundel
   * sebagai rasio terkunci (`buildBundleCartVoucherSummary`).
   */
  bundleRows?: readonly ResolvedBundle[] | null;
  /** Tanda tangan isi bundel — pilihan voucher hanya sah untuk isi bundel yang melahirkannya. */
  bundleSignature?: string;
  /** Id produk komponen bundel — ikut kandidat voucher (cakupan dijawab server). */
  bundleProductIds?: readonly string[];
}) {
  const [pricingState, setPricingState] = useState<PricingState>({ status: "loading" });
  const [reloadTick, setReloadTick] = useState(0);
  const [candidates, setCandidates] = useState<VoucherCandidate[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Pilihan tersimpan dibaca SEJAK render pertama, bukan di efek: blok ini
  // di-remount sesudah checkout ditolak `VOUCHER_INVALID`, dan render pertama
  // tanpa pilihan melaporkan `pending=false` — tombol checkout sempat aktif
  // tanpa voucher (bughunt D11-2). Efek di bawah tetap menyelaraskan saat
  // pelanggan berganti.
  const [initialPick] = useState(() => readCartVoucherPickFor(customer.id));
  const [selectedId, setSelectedId] = useState<string | null>(initialPick?.voucherId ?? null);
  const [selectedName, setSelectedName] = useState<string>(initialPick?.voucherName ?? "Voucher");
  const [selectedCode, setSelectedCode] = useState<string | null>(initialPick?.code ?? null);
  const [codeEntry, setCodeEntry] = useState(false);
  const [selection, setSelection] = useState<BoundSelection | null>(null);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState("");
  // Pratinjau ditahan batas salah ketik (mig 7000): pilihan tetap, checkout
  // menunggu, pembeli bisa mencoba lagi atau melepas (bughunt D11-1).
  const [held, setHeld] = useState("");
  const [previewTick, setPreviewTick] = useState(0);
  const previewSeq = useRef(0);
  const onCoverageRef = useRef(onCoverageChange);
  onCoverageRef.current = onCoverageChange;
  const onPendingRef = useRef(onPendingChange);
  onPendingRef.current = onPendingChange;
  // Nama voucher terpilih dibaca efek pratinjau lewat REF: menaruh `candidates`
  // (atau `selectedName`) di deps efek itu berarti satu RPC `preview_my_voucher`
  // ekstra tiap kali daftar kandidat mendarat.
  const selectedNameRef = useRef(selectedName);
  selectedNameRef.current = selectedName;

  // Pilihan dari halaman /keranjang/voucher (localStorage) — hanya milik pembeli
  // yang sedang login; divalidasi di bawah.
  useEffect(() => {
    const pick = readCartVoucherPickFor(customer.id);
    if (!pick) return;
    setSelectedId(pick.voucherId);
    setSelectedName(pick.voucherName);
    setSelectedCode(pick.code);
  }, [customer.id]);

  // Toko punya voucher PRODUK ber-kode yang hidup? → ajakan "Punya kode voucher?".
  useEffect(() => {
    let alive = true;
    void fetchMyVoucherCodeKinds().then((kinds) => {
      if (alive) setCodeEntry(kinds.includes("product"));
    });
    return () => {
      alive = false;
    };
  }, [customer.id]);

  // Kunci efek berupa STRING: `carts` array baru tiap render (idiom CartCheckout).
  const cartSignature = useMemo(
    () =>
      JSON.stringify(
        carts.map((c) => ({
          p: c.productId,
          l: c.lines.map((l) => [l.variationId, l.qty])
        }))
      ),
    [carts]
  );
  const productIdsKey = useMemo(
    () => [...new Set([...carts.map((c) => c.productId), ...(bundleProductIds ?? [])])].sort().join(","),
    [carts, bundleProductIds]
  );
  // Bundel (mig 8040): pilihan voucher diikat pada isi keranjang biasa DAN isi
  // bundel. Tanpa bundel = `cartSignature` persis seperti sebelumnya.
  const hasBundles = bundleRows !== undefined;
  const selectionSig = hasBundles ? `${cartSignature}#${bundleSignature}` : cartSignature;
  const groupSlot = customer.group?.slot ?? null;

  // Harga tiap baris (resolver checkout) — dimuat ulang tiap isi keranjang /
  // grup pelanggan berubah, dijeda 300 ms supaya stepper qty tak menembak RPC
  // pada tiap ketukan. Statusnya disimpan bersama tanda tangan keranjang yang
  // melahirkannya: hasil generasi lain tak boleh dipakai.
  useEffect(() => {
    let alive = true;
    const carts0 = carts;
    const sig = cartSignature;
    setPricingState({ status: "loading" });
    const id = setTimeout(() => {
      resolveCartsPricing(carts0, customer.id)
        .then((rows) => {
          if (alive) setPricingState({ status: "ready", sig, rows });
        })
        .catch(() => {
          if (alive) setPricingState({ status: "error", sig });
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(id);
    };
    // cartSignature merangkum isi `carts`; groupSlot memaksa resolusi ulang saat level berubah.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartSignature, customer.id, groupSlot, reloadTick]);

  useEffect(() => {
    let alive = true;
    const ids = productIdsKey ? productIdsKey.split(",") : [];
    fetchMyVoucherCandidates("product", ids)
      .then((rows) => {
        if (!alive) return;
        setCandidates(rows);
        setLoaded(true);
      })
      .catch(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [productIdsKey, groupSlot]);

  // Nama voucher terpilih disegarkan dari kandidat (tanpa RPC) — dipakai kalimat
  // "… dilepas: …" dan keadaan "memeriksa".
  useEffect(() => {
    if (!selectedId) return;
    const name = candidates.find((c) => c.voucher.id === selectedId)?.voucher.name;
    if (name) setSelectedName(name);
  }, [candidates, selectedId]);

  // Produk yang tercakup minimal satu voucher yang boleh dicoba → keterangan
  // "Terdapat voucher untuk produk ini" di kartu produk.
  const coveredProductIds = useMemo(() => {
    const out = new Set<string>();
    for (const c of candidates) {
      if (c.blockedReason) continue;
      for (const id of c.coveredProductIds) out.add(id);
    }
    return out;
  }, [candidates]);
  useEffect(() => {
    onCoverageRef.current?.(coveredProductIds);
  }, [coveredProductIds]);

  // Harga yang SAH untuk isi keranjang saat ini (null = menunggu / gagal).
  const cartRows = pricingState.status === "ready" && pricingState.sig === cartSignature ? pricingState.rows : null;
  // + komponen bundel ber-harga TERPISAH (K5). Tanpa bundel = `cartRows` apa adanya.
  const pricedRows = useMemo<ResolvedCartPricing[] | null>(() => {
    if (!hasBundles) return cartRows;
    if (!cartRows || !bundleRows) return null;
    return [
      ...cartRows,
      ...bundleRows.map((b) => ({ productId: b.bundle.id, productName: b.bundle.name, lines: bundleVoucherLines([b]) }))
    ];
  }, [hasBundles, cartRows, bundleRows]);
  const pricingFailed = pricingState.status === "error" && pricingState.sig === cartSignature;
  const emptyCart = pricedRows !== null && !pricedRows.some((c) => c.lines.length > 0);

  // Angka pratinjau hanya sah untuk generasi harga yang melahirkannya.
  const activeSelection = useMemo(
    () => (selection && selection.sig === selectionSig && pricedRows !== null ? selection : null),
    [selection, selectionSig, pricedRows]
  );
  useEffect(() => {
    setSelection((s) => (s && (s.sig !== selectionSig || pricedRows === null) ? null : s));
  }, [selectionSig, pricedRows]);

  // Pratinjau OTORITATIF untuk voucher terpilih — diulang tiap harga baris
  // berubah; gagal = voucher dilepas + sebabnya ditampilkan.
  useEffect(() => {
    if (!selectedId) {
      setSelection(null);
      setChecking(false);
      return;
    }
    if (!pricedRows) {
      // Harga belum siap / gagal dimuat: tak ada dasar memvalidasi. Angka lama
      // sudah dibuang efek di atas — UI memajang "memeriksa" atau pesan gagal.
      setChecking(false);
      return;
    }
    const rpcLines = pricedRows.flatMap((c) => c.lines);
    if (!rpcLines.length) {
      // Keranjang memang kosong — tak ada yang bisa dipotong; pilihan disimpan
      // untuk dipakai begitu ada produk.
      setChecking(false);
      return;
    }
    const sig = selectionSig;
    const seq = ++previewSeq.current;
    setChecking(true);
    previewMyVoucher(selectedId, rpcLines, selectedCode)
      .then((res) => {
        if (seq !== previewSeq.current) return;
        if (res.ok) {
          setSelection({ voucherId: selectedId, voucherName: res.voucherName, code: selectedCode, preview: res, sig });
          setSelectedName(res.voucherName);
          writeCartVoucherPick({
            voucherId: selectedId,
            voucherName: res.voucherName,
            customerId: customer.id,
            code: selectedCode
          });
          setNotice("");
          setHeld("");
        } else if (voucherPreviewHoldsPick(res.reason)) {
          // Kode sah pun dijawab begini selama terkunci: TAHAN, jangan lepas.
          setSelection(null);
          setHeld(res.message);
        } else {
          setHeld("");
          setSelectedId(null);
          setSelectedCode(null);
          setSelection(null);
          clearCartVoucherPick();
          setNotice(`${selectedNameRef.current} dilepas: ${res.message}`);
        }
      })
      .catch(() => {
        if (seq !== previewSeq.current) return;
        setSelectedId(null);
        setSelectedCode(null);
        setSelection(null);
        clearCartVoucherPick();
        setNotice("Voucher dilepas: pratinjau gagal dimuat. Pilih lagi dari daftar voucher.");
      })
      .finally(() => {
        if (seq === previewSeq.current) setChecking(false);
      });
  }, [selectedId, selectedCode, pricedRows, selectionSig, customer.id, previewTick]);

  // Ringkasan angka. Keranjang ber-bundel: baris biasa + komponen ber-harga
  // terpisah lewat mesin ringkasan yang sama, lalu langkah bundel (K5).
  const summary = useMemo<CartVoucherSummary | null>(() => {
    if (!pricedRows) return null;
    if (hasBundles && cartRows && bundleRows) {
      return buildBundleCartVoucherSummary(cartRows, bundleRows, activeSelection);
    }
    return buildCartVoucherSummary(pricedRows, activeSelection);
  }, [pricedRows, hasBundles, cartRows, bundleRows, activeSelection]);

  // Kabarkan pilihan & ringkasan ke pemanggil (CartCheckout).
  useEffect(() => {
    onSelectionChange(activeSelection);
  }, [activeSelection, onSelectionChange]);
  useEffect(() => {
    if (!candidates.length && !activeSelection && !selectedId) {
      onSummaryChange(null);
      return;
    }
    onSummaryChange(summary);
  }, [summary, activeSelection, candidates.length, selectedId, onSummaryChange]);

  // Voucher terpilih yang angkanya belum pasti → tombol checkout ditahan.
  // Keranjang kosong tak menahan apa pun (checkout-nya sudah mustahil).
  const pending = checking || (Boolean(selectedId) && !activeSelection && !emptyCart);
  useEffect(() => {
    onPendingRef.current?.(pending);
  }, [pending]);
  useEffect(() => () => onPendingRef.current?.(false), []);

  const release = () => {
    setSelectedId(null);
    setSelectedCode(null);
    setSelection(null);
    clearCartVoucherPick();
    setNotice("");
    setHeld("");
  };

  // Blok tetap dirender selama ada sesuatu untuk dikatakan: kandidat, pilihan
  // yang sedang diperiksa, atau kalimat "… dilepas: …" — voucher yang gugur
  // TIDAK dipulangkan RPC kandidat, jadi menggerbang pada `candidates` saja
  // membuat pesan sebabnya tak pernah terlihat.
  const relevant =
    candidates.length > 0 || codeEntry || Boolean(selectedId) || Boolean(activeSelection) || Boolean(notice);
  if (!loaded || !relevant) return null;

  const coveredCount = candidates.filter((c) => !c.blockedReason && c.coveredProductIds.length > 0).length;

  return (
    <div className="space-y-2 rounded-xl border border-neutral-100 p-3">
      <div className="flex items-center gap-2">
        <TicketPercent size={16} className="shrink-0 text-shopee" />
        <h3 className="text-sm font-extrabold text-ink">Voucher</h3>
        <span className="text-[11px] font-semibold text-muted">
          {activeSelection ? "1 dipakai" : candidates.length > 0 ? `${candidates.length} tersedia` : "pakai kode"}
        </span>
        {checking ? <span className="ml-auto text-[10px] font-semibold text-muted">memeriksa…</span> : null}
      </div>

      {notice ? (
        <div className="flex items-start gap-2 rounded-lg bg-shell px-2.5 py-2">
          <p className="min-w-0 flex-1 text-[11px] font-bold text-shopee">{notice}</p>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Tutup pesan voucher"
            className="-mr-0.5 shrink-0 rounded p-0.5 text-muted"
          >
            <X size={12} />
          </button>
        </div>
      ) : null}

      {activeSelection ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-shopee/40 bg-shopeeSoft/40 px-3 py-2">
          <div className="min-w-0">
            <p className="flex items-center gap-1 truncate text-xs font-extrabold text-ink">
              <CheckCircle2 size={13} className="shrink-0 text-leaf" />
              {activeSelection.voucherName}
              {activeSelection.code ? (
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-white px-1 py-px text-[9px] font-extrabold text-shopee">
                  <KeyRound size={9} /> {activeSelection.code}
                </span>
              ) : null}
            </p>
            <p className="text-[11px] font-bold text-leaf">Hemat {formatPrice(activeSelection.preview.amount)}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Link
              href="/keranjang/voucher"
              className="rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-shopee shadow-sm"
            >
              Ganti
            </Link>
            <button
              type="button"
              onClick={release}
              className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-ink shadow-sm"
            >
              <X size={11} /> Lepas
            </button>
          </div>
        </div>
      ) : selectedId ? (
        // Voucher sudah dipilih tapi angkanya belum pasti — empat keadaan yang
        // dulu sama-sama diam: ditahan batas · harga gagal · keranjang kosong ·
        // sedang diperiksa.
        held && !checking ? (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-shell px-3 py-2">
            <p className="min-w-0 text-[11px] font-bold text-ink">
              {selectedName} ditahan — {held} Lepas voucher bila ingin memesan tanpa potongan.
            </p>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setHeld("");
                  setPreviewTick((t) => t + 1);
                }}
                className="rounded-lg bg-shopee px-2.5 py-1.5 text-[11px] font-extrabold text-white shadow-sm"
              >
                Coba lagi
              </button>
              <button
                type="button"
                onClick={release}
                className="rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-ink shadow-sm"
              >
                Lepas
              </button>
            </div>
          </div>
        ) : pricingFailed ? (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-shell px-3 py-2">
            <p className="min-w-0 text-[11px] font-bold text-ink">
              Harga keranjang gagal dimuat — {selectedName} belum bisa dihitung.
            </p>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => setReloadTick((t) => t + 1)}
                className="rounded-lg bg-shopee px-2.5 py-1.5 text-[11px] font-extrabold text-white shadow-sm"
              >
                Coba lagi
              </button>
              <button
                type="button"
                onClick={release}
                className="rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-ink shadow-sm"
              >
                Lepas
              </button>
            </div>
          </div>
        ) : emptyCart ? (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-shell px-3 py-2">
            <p className="min-w-0 text-[11px] font-bold text-ink">
              {selectedName} menunggu isi keranjang — potongannya dihitung setelah ada produk.
            </p>
            <button
              type="button"
              onClick={release}
              className="shrink-0 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-ink shadow-sm"
            >
              Lepas
            </button>
          </div>
        ) : (
          // "Lepas" tetap tersedia: selama pemeriksaan berjalan tombol
          // "Keep Produk" ditahan, jadi pembeli harus punya jalan
          // keluar kalau pratinjaunya berlarut.
          <div className="flex items-center justify-between gap-2 rounded-lg bg-shell px-3 py-2">
            <p className="min-w-0 text-[11px] font-bold text-muted">
              Memeriksa {selectedName} untuk keranjang ini…
            </p>
            <button
              type="button"
              onClick={release}
              className="shrink-0 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-ink shadow-sm"
            >
              Lepas
            </button>
          </div>
        )
      ) : (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-shell px-3 py-2">
          <p className="text-xs font-bold text-ink">
            {coveredCount > 0
              ? `Terdapat ${coveredCount} voucher untuk produk di keranjang ini`
              : candidates.length > 0
                ? "Ada voucher untuk akun ini — cek syaratnya"
                : "Punya kode voucher? Masukkan untuk dapat potongan."}
          </p>
          <Link
            href="/keranjang/voucher"
            className="shrink-0 rounded-lg bg-shopee px-3 py-1.5 text-[11px] font-extrabold text-white shadow-sm"
          >
            {candidates.length > 0 ? "Pakai Voucher" : "Masukkan Kode"}
          </Link>
        </div>
      )}
      {codeEntry && candidates.length > 0 && !selectedId ? (
        <p className="text-[10px] font-semibold text-muted">
          Punya kode voucher? Ketik kodenya di halaman Pakai Voucher.
        </p>
      ) : null}

      {summary ? <CartVoucherSummaryCard summary={summary} /> : null}
      <p className="text-[10px] font-medium text-muted">
        Voucher dipotong dari harga umum, lalu potongan promo dan diskon level dihitung. Maksimal satu voucher per checkout.
      </p>
      {hasBundles ? (
        <p className="text-[10px] font-medium text-muted">
          Untuk bundling, voucher dihitung dari harga satuan barangnya, lalu harga bundling diterapkan dengan perbandingan
          yang sama.
        </p>
      ) : null}
    </div>
  );
}
